import {
  computed,
  ref,
  watch,
  type Ref,
} from 'vue'
import { MENU_HEIGHT } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuScroll/constants'

/** Sentinel IO → position: fixed под видимой шапкой */
export default function createSectionMenuSticky(headerOffset: Ref<number>) {
  const root = ref<HTMLElement>()
  const anchor = ref<HTMLElement>()
  const sentinel = ref<HTMLElement>()
  const isPinned = ref(false)
  const pinnedTop = ref(0)
  const menuHeight = ref(MENU_HEIGHT)

  let stickyObserver: IntersectionObserver | null = null
  let menuResizeObserver: ResizeObserver | null = null
  let observedMenuRoot: HTMLElement | null = null
  let appliedMargin = Number.NaN

  const anchorStyle = computed(() => (
    isPinned.value ? { height: `${menuHeight.value}px` } : undefined
  ))

  const pinnedStyle = computed(() => (
    isPinned.value ? { top: `${pinnedTop.value}px` } : undefined
  ))

  function syncMenuHeight() {
    menuHeight.value = root.value?.offsetHeight || MENU_HEIGHT
  }

  function bindMenuResize() {
    if (!root.value) {
      return
    }

    if (observedMenuRoot === root.value && menuResizeObserver) {
      syncMenuHeight()

      return
    }

    menuResizeObserver?.disconnect()
    observedMenuRoot = root.value
    syncMenuHeight()

    menuResizeObserver = new ResizeObserver(entries => {
      const entry = entries[0]

      if (!entry) {
        return
      }

      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height

      menuHeight.value = height || MENU_HEIGHT
    })
    menuResizeObserver.observe(root.value)
  }

  function setup() {
    if (!sentinel.value) {
      return
    }

    bindMenuResize()

    const marginTop = headerOffset.value

    if (stickyObserver && marginTop === appliedMargin) {
      return
    }

    stickyObserver?.disconnect()

    stickyObserver = new IntersectionObserver(([entry]) => {
      if (!entry || document.documentElement.classList.contains('html-scroll-blocked')) {
        return
      }

      const offset = headerOffset.value
      const { top, bottom } = entry.boundingClientRect
      const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight
      // За верх (ниже видимой шапки) → pin; за низ экрана → нет
      const pinned = bottom <= offset && top < rootBottom

      isPinned.value = pinned
      pinnedTop.value = pinned ? offset : 0
    }, {
      rootMargin: `-${marginTop}px 0px 0px 0px`,
      threshold: 0,
    })

    stickyObserver.observe(sentinel.value)
    appliedMargin = marginTop
  }

  function teardown() {
    stickyObserver?.disconnect()
    stickyObserver = null
    menuResizeObserver?.disconnect()
    menuResizeObserver = null
    observedMenuRoot = null
    appliedMargin = Number.NaN
  }

  // Смена видимости шапки: другой rootMargin и top у pinned-меню
  watch(headerOffset, nextOffset => {
    if (isPinned.value) {
      pinnedTop.value = nextOffset
    }

    setup()
  })

  return {
    root,
    anchor,
    sentinel,
    isPinned,
    pinnedTop,
    menuHeight,
    anchorStyle,
    pinnedStyle,
    setup,
    teardown,
  }
}
